// Auth helpers - token storage + axios interceptor + authenticated downloads
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const TOKEN_KEY = 'mv_token';
const USER_KEY  = 'mv_user';

export const api = axios.create({ baseURL: API });

api.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  r => r,
  err => {
    // A 401 during an auth flow (login, MFA, invitation, reset) is an expected
    // "bad credentials" response: let the calling screen show the error inline.
    // Only a 401 on a normal app request means the session expired, in which case
    // the session is cleared and the page reloads. This stops a failed login from
    // reloading the page, which looked like the login screen flickering.
    const url = err.config?.url || '';
    const isAuthFlow = /\/auth\/login|\/mfa\/verify|\/mfa\/enrol|\/auth\/accept-invitation|\/auth\/complete-password-reset/.test(url);
    if (err.response?.status === 401 && !isAuthFlow) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      const p = window.location.pathname;
      if (!p.startsWith('/invite/') && !p.startsWith('/reset/')) window.location.reload();
    }
    return Promise.reject(err);
  }
);

export const auth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getUser: () => { try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; } },
  setSession: (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  },

  async login(email, password) {
    const form = new URLSearchParams();
    form.append('username', email);
    form.append('password', password);
    const r = await api.post('/auth/login', form, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
    if (r.data.mfa_required) {
      // Caller must continue with auth.completeMFA(...)
      return { mfa_required: true, mfa_pending_token: r.data.mfa_pending_token };
    }
    if (r.data.mfa_enrollment_required) {
      // Company requires 2FA and this user has not enrolled. Clear any stale token so
      // the enrol calls send the enrol token, then let the caller drive the gate.
      auth.logout();
      return { mfa_enrollment_required: true, enrol_pending_token: r.data.enrol_pending_token };
    }
    auth.setSession(r.data.access_token, r.data.user);
    return r.data.user;
  },

  async completeMFA(mfaPendingToken, code, isBackupCode = false) {
    const r = await api.post('/mfa/verify', { code, is_backup_code: isBackupCode },
      { headers: { Authorization: `Bearer ${mfaPendingToken}` } });
    const access = r.data.access_token;
    // Fetch user profile with the new token
    const userR = await api.get('/auth/me', { headers: { Authorization: `Bearer ${access}` } });
    auth.setSession(access, userR.data);
    return { user: userR.data, backup_codes_remaining: r.data.backup_codes_remaining };
  },

  // 2FA enrolment. Pass the short-lived enrol token when there is no session yet
  // (forced enrolment at sign-in). Omit it to use the current session (in-app gate or
  // voluntary setup). On confirm the backend returns a fresh full session.
  async mfaEnrolStart(enrolToken) {
    const cfg = enrolToken ? { headers: { Authorization: `Bearer ${enrolToken}` } } : {};
    const r = await api.post('/mfa/enrol/start', {}, cfg);
    return r.data;  // { secret, provisioning_uri, qr_code_url }
  },

  async mfaEnrolQr(enrolToken) {
    const cfg = enrolToken
      ? { responseType: 'blob', headers: { Authorization: `Bearer ${enrolToken}` } }
      : { responseType: 'blob' };
    const r = await api.get('/mfa/enrol/qr', cfg);
    return URL.createObjectURL(r.data);
  },

  async mfaEnrolConfirm(enrolToken, code, password) {
    const cfg = enrolToken ? { headers: { Authorization: `Bearer ${enrolToken}` } } : {};
    const r = await api.post('/mfa/enrol/confirm', { code, password }, cfg);
    const access = r.data.access_token;
    const userR = await api.get('/auth/me', { headers: { Authorization: `Bearer ${access}` } });
    auth.setSession(access, userR.data);
    return { user: userR.data, backup_codes: r.data.backup_codes };
  },

  /** Authenticated download - fetches with bearer token, opens save dialog. */
  async downloadAuthenticated(url, filename) {
    const r = await api.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || url.split('/').pop();
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },
};
